import React from 'react';

import {
  Alert,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';

import {
  IAllocationBudgetControl,
  IAllocationSummary,
} from './budgetallocation.models';

type Props={
  summary:IAllocationSummary;
  control:IAllocationBudgetControl;
};

const money=(value:any)=>
  Number(value||0).toLocaleString(
    'fr-FR',
    {maximumFractionDigits:0},
  );

class BudgetAllocationSummaryPanel
extends React.PureComponent<Props>{

  private metric=(label:string,value:any,caption?:string)=>(
    <Grid size={{xs:6,sm:4,md:2}} key={label}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" sx={{fontWeight:700}}>
        {value??0}
      </Typography>
      {caption&&(
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Grid>
  );

  render(){
    const summary=this.props.summary||{};
    const control=this.props.control||{} as IAllocationBudgetControl;

    const hasControl=Boolean(control.budgetcontrolid);
    const utilisation=Math.max(
      0,
      Math.min(100,Number(control.utilisationpercent||0)),
    );

    return (
      <Stack spacing={2}>
        {!hasControl&&(
          <Alert severity="warning">
            No active Activity Budget Control ceiling is available for this Planning Cycle.
            Allocation may be reviewed, but material allocation must not be completed until Budget Control is active.
          </Alert>
        )}

        {hasControl&&(
          <Card elevation={1}>
            <CardContent>
              <Typography variant="subtitle1" sx={{fontWeight:700,mb:1}}>
                Activity Budget Control
              </Typography>

              <Grid container spacing={2}>
                {this.metric(
                  'Activity Ceiling',
                  money(control.ceilingamount),
                  control.currencycode||'XOF',
                )}
                {this.metric(
                  'Activity Budget',
                  money(control.allocatedamount),
                  'Authoritative task-budget usage',
                )}
                {this.metric(
                  'Available',
                  money(control.availableamount),
                )}
                {this.metric(
                  'Utilisation',
                  `${Number(control.utilisationpercent||0).toFixed(2)}%`,
                )}
                {this.metric(
                  'Warning At',
                  `${Number(control.warningpercent||0).toFixed(0)}%`,
                )}
                {this.metric(
                  'Control Status',
                  control.controlstatus||'UNAVAILABLE',
                )}
              </Grid>

              <LinearProgress
                variant="determinate"
                value={utilisation}
                color={
                  control.controlstatus==='EXCEEDED'
                    ?'error'
                    :control.controlstatus==='WARNING'
                      ?'warning'
                      :'success'
                }
                sx={{mt:2,height:8,borderRadius:4}}
              />
            </CardContent>
          </Card>
        )}

        <Card elevation={1}>
          <CardContent>
            <Typography variant="subtitle1" sx={{fontWeight:700,mb:1}}>
              Allocation Readiness
            </Typography>

            <Grid container spacing={2}>
              {this.metric('Cost Centers',summary.costcenters)}
              {this.metric('Activities',summary.activities)}
              {this.metric('Allocated',summary.allocated_activities)}
              {this.metric('Unallocated',summary.unallocated_activities)}
              {this.metric('Links',summary.allocation_links)}
              {this.metric(
                'Mapped Amount',
                money(summary.allocated_amount),
              )}
            </Grid>
          </CardContent>
        </Card>
      </Stack>
    );
  }
}

export default BudgetAllocationSummaryPanel;
